import { useEffect, useMemo, useState } from "react";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";

import { PropsWithClassName } from "../../common/types/PropsWithClassName";
import { Category } from "./Category";
import { TicketType } from "../ticketType/TicketType";
import { fetchCategoriesByDepartment } from "./categoriesApi";
import NoResultsFound from "../../common/components/noResultsFound/NoResultsFound";

interface CategoriesDropdownProps extends PropsWithClassName {
  column?: boolean;
  disabled?: boolean;
  departmentId: string;
  ticketType?: TicketType;
  firstLevelValue?: string;
  secondLevelValue?: string;
  thirdLevelValue?: string;
  onChangeFirstLevel?: (category?: Category) => void;
  onChangeSecondLevel?: (category?: Category) => void;
  onChangeThirdLevel?: (category?: Category) => void;
  onChangeAllLevels?: (categories: Category[]) => void;
}

export default function CategoriesDropdown({
  className,
  column,
  disabled,
  departmentId,
  ticketType,
  firstLevelValue,
  secondLevelValue,
  thirdLevelValue,
  onChangeFirstLevel,
  onChangeSecondLevel,
  onChangeThirdLevel,
  onChangeAllLevels,
}: CategoriesDropdownProps) {
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [selectedFirstLevelCategory, setSelectedFirstLevelCategory] = useState<Category | undefined>(undefined);
  const [selectedSecondLevelCategory, setSelectedSecondLevelCategory] = useState<Category | undefined>(undefined);
  const [selectedThirdLevelCategory, setSelectedThirdLevelCategory] = useState<Category | undefined>(undefined);

  const allFirstLevelCategories = useMemo(() => allCategories.filter((category) => category.level === 0), [allCategories]);
  const allSecondLevelCategories = useMemo(() => allCategories.filter((category) => category.level === 1), [allCategories]);
  const allThirdLevelCategories = useMemo(() => allCategories.filter((category) => category.level === 2), [allCategories]);

  const secondLevelCategories = useMemo(() => {
    return allSecondLevelCategories.filter((secondLevelCategory) => {
      const hasChildThirdLevelCategories = allThirdLevelCategories.some((thirdLevelCategory) => {
        if (ticketType == null) {
          return secondLevelCategory.id === thirdLevelCategory.parentId;
        }
        return secondLevelCategory.id === thirdLevelCategory.parentId && thirdLevelCategory.ticketType === ticketType;
      });
      return secondLevelCategory.parentId === selectedFirstLevelCategory?.id && hasChildThirdLevelCategories;
    });
  }, [allThirdLevelCategories, allSecondLevelCategories, selectedFirstLevelCategory, ticketType]);

  const thirdLevelCategories = useMemo(() => {
    return allCategories.filter((category) => {
      if (ticketType == null) {
        return category.parentId === selectedSecondLevelCategory?.id;
      }
      return category.parentId === selectedSecondLevelCategory?.id && category.ticketType === ticketType;
    });
  }, [allCategories, selectedSecondLevelCategory, ticketType]);

  useEffect(() => {
    const fetchCategories = async () => {
      if (departmentId) {
        const allCategories = await fetchCategoriesByDepartment(departmentId);
        const activeCategories = allCategories.filter((category) => (category.id !== thirdLevelValue ? category.isActive : true));
        setAllCategories(activeCategories);
      }
    };
    fetchCategories();
  }, [departmentId]);

  useEffect(() => {
    if (!allThirdLevelCategories.length) {
      return;
    }

    const thirdLevelCategory = allThirdLevelCategories.find((category) => category.id === thirdLevelValue);
    const secondLevelCategory = allSecondLevelCategories.find(
      (category) => category.id === secondLevelValue || category.id === thirdLevelCategory?.parentId
    );
    const firstLevelCategory = allFirstLevelCategories.find(
      (category) => category.id === firstLevelValue || category.id === secondLevelCategory?.parentId
    );
    setSelectedFirstLevelCategory(firstLevelCategory || selectedFirstLevelCategory);
    setSelectedSecondLevelCategory(secondLevelCategory || selectedSecondLevelCategory);
    handleThirdLevelCategoryChange(thirdLevelCategory);
  }, [allThirdLevelCategories, thirdLevelValue]);

  useEffect(() => {
    if (!onChangeAllLevels) {
      return;
    }
    if (allCategories.length === 0) {
      return;
    }
    const categories = [selectedFirstLevelCategory, selectedSecondLevelCategory, selectedThirdLevelCategory].filter(Boolean) as Category[];
    onChangeAllLevels(categories);
  }, [selectedFirstLevelCategory, selectedSecondLevelCategory, selectedThirdLevelCategory]);

  const handleFirstLevelCategoryChange = (category?: Category) => {
    setSelectedFirstLevelCategory(category);
    onChangeFirstLevel?.(category);
  };

  const handleSecondLevelCategoryChange = (category?: Category) => {
    setSelectedSecondLevelCategory(category);
    onChangeSecondLevel?.(category);
  };

  const handleThirdLevelCategoryChange = (category?: Category) => {
    setSelectedThirdLevelCategory(category);
    if (thirdLevelValue !== category?.id) {
      onChangeThirdLevel?.(category);
    }
  };

  const onFirstLevelCategoryChange = (e: DropdownChangeEvent) => {
    handleFirstLevelCategoryChange(e.value);
    handleSecondLevelCategoryChange(undefined);
    handleThirdLevelCategoryChange(undefined);
  };

  const onSecondLevelCategoryChange = (e: DropdownChangeEvent) => {
    handleSecondLevelCategoryChange(e.value);
    handleThirdLevelCategoryChange(undefined);
  };

  const onThirdLevelCategoryChange = (e: DropdownChangeEvent) => {
    handleThirdLevelCategoryChange(e.value);
  };

  return (
    <div className={`${className} ${column ? "flex-column" : "flex-row"} flex flex-wrap md:flex-nowrap gap-2`}>
      <Dropdown
        className={`${column ? "w-full" : "w-full md:w-4"}`}
        optionLabel="name"
        scrollHeight="300px"
        disabled={disabled}
        options={allFirstLevelCategories}
        value={selectedFirstLevelCategory}
        emptyMessage={<NoResultsFound />}
        onChange={onFirstLevelCategoryChange}
      />
      <Dropdown
        className={`${column ? "w-full" : "w-full md:w-4"}`}
        optionLabel="name"
        scrollHeight="300px"
        disabled={disabled}
        options={secondLevelCategories}
        value={selectedSecondLevelCategory}
        emptyMessage={<NoResultsFound />}
        onChange={onSecondLevelCategoryChange}
      />
      <Dropdown
        className={`${column ? "w-full" : "w-full md:w-4"}`}
        optionLabel="name"
        scrollHeight="300px"
        disabled={disabled}
        options={thirdLevelCategories}
        value={selectedThirdLevelCategory}
        emptyMessage={<NoResultsFound />}
        onChange={onThirdLevelCategoryChange}
      />
    </div>
  );
}
